import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { SkillIcon } from 'atoms';
import { LessonSkill } from 'components/types/lesson';
import { SkillDemandLine } from 'molecules/SkillDemandLine';
import { skillMapHref } from 'router/careerLinks';
import fontService, { FontWeight } from 'services/FontService';
import { mobile } from 'services/ui';

interface ILessonSkills {
  skills: LessonSkill[];
  /** Занятие прошло → навык уже у студента. Признак с сервера, не по дате. */
  granted?: boolean;
}

/**
 * Что даёт занятие — на самом занятии (ТЗ 0040, доводка; расширено в 0041).
 *
 * Владелец: «пусть для каждого занятия студенты видят навыки, которые они
 * приобретут сейчас». До этого механизм работал молча: навык появлялся в
 * «Моём росте» задним числом, а на занятии про него не было ни слова —
 * студент учился в одном месте, а результат видел в другом.
 *
 * Две формулировки, а не одна с галочкой: «получите» — это обещание, ради
 * которого стоит прийти, «получили» — уже итог. Одна нейтральная фраза не
 * работает ни там, ни там.
 *
 * 🔴 Навык — КАРТОЧКА, а не чип с одним словом. Слово «Docker» ничего не
 * говорит студенту, который пришёл его изучать: он не знает ни что это, ни
 * зачем, ни насколько это ценно на рынке. Все три ответа уже лежали в базе —
 * описание, уровень занятия и снимок востребованности, — и не спрашивались
 * этим экраном. Имя ведёт в паспорт навыка: занятие перестаёт быть тупиком.
 */
export const LessonSkills = memo(({ skills, granted }: ILessonSkills) => {
  const { t } = useTranslation();

  /**
   * Подпись уровня — ровно то, что начислится, и ни уровнем больше.
   *
   * 🔴 Чисел два, а не одно. Пока курс идёт, занятие открывает навык
   * минимальным уровнем, а уровень набора (3-5) приходит только по завершении
   * ВСЕГО курса: так устроено начисление, и другого нет. Печатать здесь
   * уровень набора значило бы обещать студенту то, чего он сегодня не
   * получит — ровно ошибка, от которой предостерегает признак «занятие
   * прошло» рядом. Сервер отдаёт оба числа и сам решает, пройден ли курс;
   * совпали — значит курс уже позади, и уровень окончательный.
   */
  const levelWords = (skill: LessonSkill): string | null => {
    const now = skill.unlockLevel;
    if (!now) return null;
    const full = skill.courseLevel;
    if (!full || full === now) {
      return granted
        ? t('lesson.skillLevelGot', { n: now })
        : t('lesson.skillLevelWillGet', { n: now });
    }
    return granted
      ? t('lesson.skillLevelGotPartial', { n: now, full })
      : t('lesson.skillLevelWillGetPartial', { n: now, full });
  };

  // Занятие без навыков — обычное дело: молчим, а не показываем пустую плашку.
  if (!skills.length) return null;

  return (
    <Wrapper $granted={Boolean(granted)}>
      <Title>{granted ? t('lesson.skillsGot') : t('lesson.skillsWillGet')}</Title>
      <List>
        {skills.map((skill) => (
          <Card key={skill.id}>
            <SkillIcon
              slug={skill.slug}
              name={skill.name}
              category={skill.category}
              iconUrl={skill.iconUrl}
              size={26}
            />
            <Body>
              <NameLink to={skillMapHref({ skill: skill.slug })}>{skill.name}</NameLink>
              {skill.description ? <About>{skill.description}</About> : null}
              <Meta>
                {skill.unlockLevel ? <Level>{levelWords(skill)}</Level> : null}
                <SkillDemandLine demand={skill.demand ?? null} />
              </Meta>
            </Body>
          </Card>
        ))}
      </List>
      <Note>{granted ? t('lesson.skillsGotNote') : t('lesson.skillsWillGetNote')}</Note>
    </Wrapper>
  );
});

LessonSkills.displayName = 'LessonSkills';

const Wrapper = styled.div<{ $granted: boolean }>`
  margin-bottom: 24px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid ${({ $granted }) => ($granted ? Colors.signal_green : Colors.system_gray)};
  background: ${Colors.bg_gray};
`;

const Title = styled.div`
  ${fontService.text({ size: 14, weight: FontWeight.bold })};
  color: ${Colors.black};
  margin-bottom: 12px;
`;

/*
 * Две колонки, потому что карточка выше чипа: одной колонкой десяток навыков
 * уводит описание занятия под сгиб. В проекте нет глобального
 * box-sizing: border-box, поэтому ширину задаёт grid, а не проценты с padding.
 */
const List = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  ${mobile} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${Colors.white};
  border: 1px solid ${Colors.system_gray};
`;

/* min-width: 0 — иначе длинное описание распирает колонку grid наружу. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const NameLink = styled(Link)`
  ${fontService.text({ size: 13, weight: FontWeight.medium })};
  color: ${Colors.black};

  &:hover,
  &:focus {
    /* Жёлтый как цвет текста не проходит контраст, поэтому подчёркивание. */
    color: ${Colors.black};
    text-decoration: underline;
  }
`;

const About = styled.span`
  ${fontService.text({ size: 12 })};
  color: ${Colors.gray_dark};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 2px;
`;

const Level = styled.span`
  ${fontService.text({ size: 12 })};
  color: ${Colors.gray_dark};
`;

const Note = styled.div`
  margin-top: 12px;
  ${fontService.text({ size: 12 })};
  color: ${Colors.gray_dark};
`;
